import {
  NotificationCountResponseDto,
  NotificationListResponseDto,
} from '@bitecritic-api/src/notifications/dto/notification-response.dto';
import requestsService from '../requestsService';
import { UpdateNotificationSettingsRequestDto } from '@bitecritic-api/src/notifications/dto/notification-settings.dto';
import { UserNotificationSettingsResponseDto } from '@bitecritic-api/src/notifications/dto/notification-settings.dto';

const basePath = 'notifications' as const;
const settingsPath = 'notification-settings' as const;

/**
 * Get the unread notification count for the current user
 */
export const getUnreadCount = async (): Promise<number> => {
  try {
    const response = await requestsService.fetch<NotificationCountResponseDto>(
      `${basePath}/count`,
    );
    return response?.count || 0;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    return 0;
  }
};

/**
 * Get paginated notifications for the current user
 */
export const getNotifications = async (
  page = 1,
  limit = 10,
  language?: string,
): Promise<NotificationListResponseDto> => {
  try {
    const langParam = language ? `&language=${language}` : '';
    const response = await requestsService.fetch<NotificationListResponseDto>(
      `${basePath}?page=${page}&limit=${limit}${langParam}`,
    );
    return (
      response || {
        data: [],
        meta: {
          total: 0,
          page,
          limit,
          hasNextPage: false,
          hasPreviousPage: false,
          totalPages: 0,
        },
      }
    );
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return {
      data: [],
      meta: {
        total: 0,
        page,
        limit,
        hasNextPage: false,
        hasPreviousPage: false,
        totalPages: 0,
      },
    };
  }
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId: string): Promise<void> => {
  try {
    await requestsService.put({
      url: `${basePath}/${notificationId}/read`,
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<void> => {
  try {
    await requestsService.put({
      url: `${basePath}/read-all`,
    });
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (
  notificationId: string,
): Promise<void> => {
  try {
    await requestsService.delete(`${basePath}/${notificationId}`);
  } catch (error) {
    console.error('Error deleting notification:', error);
  }
};

/**
 * Register a device for push notifications
 */
export const registerDevice = async (
  deviceToken: string,
  deviceType: 'ios' | 'android' | 'web',
): Promise<void> => {
  try {
    console.log(
      `Registering device with token: ${deviceToken}, type: ${deviceType}`,
    );
    await requestsService.post(`${basePath}/register-device`, {
      deviceToken,
      deviceType,
    });
    console.log('Device registration successful');
  } catch (error) {
    console.error('Error registering device:', error);
    throw error; // Rethrow to allow proper error handling in the caller
  }
};

/**
 * Get notification settings for the current user
 */
export const getNotificationSettings =
  async (): Promise<UserNotificationSettingsResponseDto | null> => {
    try {
      const response =
        await requestsService.fetch<UserNotificationSettingsResponseDto>(
          settingsPath,
        );
      return response || null;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  };

/**
 * Update notification settings for the current user
 */
export const updateNotificationSettings = async (
  settings: UpdateNotificationSettingsRequestDto,
): Promise<UserNotificationSettingsResponseDto> => {
  try {
    return requestsService.put<UserNotificationSettingsResponseDto>({
      url: settingsPath,
      requestBody: settings,
    });
  } catch (error) {
    console.error('Error updating notification settings:', error);
    throw error;
  }
};

export const notificationsService = {
  getUnreadCount,
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  registerDevice,
  getNotificationSettings,
  updateNotificationSettings,
};

export default notificationsService;
